<template>
  <div class="edit-tabulation">
    <v-skeleton-loader v-if="loading" type="heading, list-item-three-line, image" class="ma-3" />

    <v-alert v-else-if="loadError" type="error" text class="ma-3">
      {{ loadError }}
    </v-alert>

    <v-form v-else ref="form" v-model="valid" lazy-validation>
      <v-container fluid class="pa-2 pa-sm-3">
        <v-row dense>
          <v-col cols="12" md="4">
            <v-card outlined>
              <v-card-title class="py-2 px-3 text-subtitle-1 font-weight-medium">
                <v-icon left color="indigo">mdi-tab-edit</v-icon>
                Propriétés de la tabulation
              </v-card-title>
              <v-divider />
              <v-card-text class="pa-3">
                <TextField
                  :item="{ value: 'name', name: 'Nom de la tabulation', defaultprops: { show: true } }"
                  :keyup="(item, value) => { tabulation.name = value }"
                  :valField="tabulation.name"
                />

                <TextField
                  :item="{ value: 'class', name: 'Classe', defaultprops: { show: true } }"
                  :keyup="(item, value) => { tabulation.params.class = value }"
                  :valField="tabulation.params.class"
                />

                <TextField
                  :item="{ value: 'icon', name: 'Icône', defaultprops: { show: true } }"
                  :keyup="(item, value) => { tabulation.params.icon = value }"
                  :valField="tabulation.params.icon"
                />

                <SwitchC
                  :item="{ name: 'Mode sombre', value: 'dark', defaultprops: { show: true } }"
                  :valField="tabulation.params.dark"
                  :change="(item, value) => { tabulation.params.dark = value }"
                />

                <v-alert text dense type="info" class="text-caption">
                  Une tabulation dynamique crée ses onglets à partir du résultat d’une requête.
                </v-alert>

                <SwitchC
                  :item="{ name: 'Tabulation dynamique', value: 'setcustom', defaultprops: { show: true } }"
                  :valField="tabulation.params.setcustom"
                  :change="(item, value) => { tabulation.params.setcustom = value }"
                />

                <Select
                  v-if="tabulation.params.setcustom"
                  :item="{
                    name: 'Table ciblée par la requête',
                    value: 'table',
                    liste: listTables,
                    defaultprops: { show: true, column: false },
                  }"
                  :change="(item, value) => { tabulation.params.custom.table = value ? value.value : '' }"
                  :valField="tabulation.params.custom.table"
                />
              </v-card-text>
            </v-card>
          </v-col>

          <v-col
            v-if="tabulation.params.setcustom && tabulation.params.custom.table"
            cols="12"
            md="8"
          >
            <v-card outlined>
              <v-card-text class="pa-3">
                <Autocomplete
                  :item="{
                    name: 'Afficher une page complète',
                    value: 'idpage',
                    defaultprops: { show: true, clearable: true },
                    liste: routes,
                  }"
                  :valField="tabulation.params.idpage"
                  :change="(item, value) => { tabulation.params.idpage = value ? value.value : 0 }"
                />
                <p class="text-caption mb-2">
                  La page sélectionnée remplace le contenu manuel de l’onglet.
                </p>

                <v-tabs v-model="builderTab" show-arrows>
                  <v-tab>Filtre</v-tab>
                  <v-tab>Champs</v-tab>
                </v-tabs>
                <v-tabs-items v-model="builderTab">
                  <v-tab-item>
                    <Filtre
                      :filtre="tabulation.params.custom.filter"
                      :table="tabulation.params.custom.table"
                      @recepVal="tabulation.params.custom.filter = $event"
                    />
                  </v-tab-item>

                  <v-tab-item>
                    <v-alert text dense type="info" class="text-caption mt-2">
                      Utilisez l’alias <b>ongletname</b> pour le nom, <b>params-[clé]</b> pour les
                      paramètres et <b>querys-[clé]</b> pour les requêtes transmises à l’onglet.
                    </v-alert>
                    <Fields
                      :modalKey="editorContextKey"
                      :table="tabulation.params.custom.table"
                      :champs="tabulation.params.custom.fields"
                      :filter="tabulation.params.custom.filter"
                      :page="null"
                      @recepVal="tabulation.params.custom.fields = $event"
                    />
                  </v-tab-item>
                </v-tabs-items>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <v-card outlined class="mt-3">
          <v-card-title class="py-2 px-3 text-subtitle-1 font-weight-medium">
            <v-icon left color="indigo">mdi-code-braces</v-icon>
            Paramètres transmis au contenu
          </v-card-title>
          <v-divider />
          <v-card-text class="pa-3">
            <v-alert text dense type="info" class="text-caption">
              Ces valeurs complètent les paramètres hérités du parent. Dans une tabulation
              dynamique, les alias <b>params-[clé]</b> de la requête restent prioritaires.
            </v-alert>
            <ArrayTable
              :item="{
                otherparams: {
                  arrayTable: [
                    { type: '', key: 'key', name: 'Clé' },
                    {
                      type: 'select',
                      key: 'type',
                      name: 'Type',
                      ArrayValue: [
                        { text: 'Texte', value: 'text' },
                        { text: 'Nombre', value: 'number' },
                        { text: 'Booléen', value: 'bool' },
                        { text: 'Tableau JSON', value: 'array' },
                      ],
                    },
                    {
                      type: '',
                      key: 'value',
                      name: 'Valeur',
                      description: 'Les variables moteur comme [[params||id]] sont acceptées.',
                    },
                  ],
                },
                defaultprops: {
                  show: true,
                  tableDraggable: true,
                  tableDel: true,
                  hideFooter: true,
                  hidesearch: true,
                  addTitle: 'Ajouter un paramètre',
                },
              }"
              :valField="tabulation.params.contentParams"
              :change="(item, value) => { tabulation.params.contentParams = value }"
            />
          </v-card-text>
        </v-card>

        <v-card outlined class="mt-3">
          <v-card-actions class="justify-end pa-3">
            <v-btn
              color="primary"
              :disabled="!valid || saving"
              :loading="saving"
              @click="validate"
            >
              <v-icon left>mdi-content-save</v-icon>
              Enregistrer
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-container>
    </v-form>
  </div>
</template>

<script>
export default {
  name: 'EditTabulation',

  props: {
    moduleId: { type: [String, Number], required: true },
    tabulationId: { type: [String, Number], default: 'new' },
    contextKey: { type: String, default: '' },
    embedded: { type: Boolean, default: false },
    standalone: { type: Boolean, default: false },
  },

  components: {
    TextField: () => import('@/components/fields/textfield.vue'),
    SwitchC: () => import('@/components/fields/switch.vue'),
    Filtre: () => import('@/components/engine/outils/constructFiltre/Filtre.vue'),
    Fields: () => import('@/components/engine/outils/constructField/Field.vue'),
    Select: () => import('@/components/fields/select.vue'),
    Autocomplete: () => import('@/components/fields/autocomplete.vue'),
    ArrayTable: () => import('@/components/fields/arrayTable.vue'),
  },

  data() {
    return {
      tabulation: null,
      valid: true,
      loading: true,
      saving: false,
      loadError: '',
      builderTab: 0,
      listTables: [],
      routes: [],
      internalContextKey: `edit-tabulation-${this._uid}`,
    }
  },

  computed: {
    editorContextKey() {
      return this.contextKey || this.internalContextKey
    },
  },

  methods: {
    async loadInitialData() {
      this.loading = true
      this.loadError = ''

      try {
        const [tabulationResponse, tablesResponse] = await Promise.all([
          this.API({
            type: 'get',
            url: `engine/module/${this.moduleId}/tabulation/${this.tabulationId}/edit`,
          }),
          this.API({ type: 'get', url: 'engine/getlisttable' }),
        ])

        this.tabulation = tabulationResponse.data
        this.listTables = (tablesResponse.data || []).map(table => ({ text: table, value: table }))
        this.routes = (this.$store.getters.listAll || []).map(page => ({
          text: `${page.displayname} (${page.path})`,
          value: page.props.page,
        }))
      } catch (error) {
        this.loadError = error?.response?.data?.message || 'Impossible de charger la tabulation.'
      } finally {
        this.loading = false
      }
    },

    async validate() {
      if (!this.$refs.form.validate()) return

      this.saving = true
      this.$store.dispatch('getoverlay', true)

      try {
        const response = await this.API({
          type: 'put',
          url: `engine/module/${this.moduleId}/tabulation/${this.tabulationId}/edit`,
          params: this.tabulation,
        })

        const result = response.data || {}
        const success = result.success === true || Boolean(result.id)

        if (!success) {
          this.$toast.error(result.messageError || 'La tabulation n’a pas pu être enregistrée.')
          return
        }

        if (result.id) this.tabulation = result
        this.$emit('saved', this.tabulation)

        if (this.embedded) this.$emit('close')
        else if (this.standalone) this.$router.back()
      } finally {
        this.saving = false
        this.$store.dispatch('getoverlay', false)
      }
    },
  },

  mounted() {
    this.loadInitialData()
  },
}
</script>

<style scoped>
.edit-tabulation {
  width: 100%;
}
</style>
